import React, { useRef, useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  alpha,
  Fade,
  Skeleton
} from '@mui/material';
import { useReactToPrint } from 'react-to-print';

import { useMarketResearch } from '../hooks/useMarketResearch';
import { ReportHeader } from './ReportHeader';
import { ActionBar } from './ActionBar';
import { ReportContent } from './ReportContent';
import { PrintHeader } from './PrintHeader';
import { PrintFooter } from './PrintFooter';
import { ProfessionalMetrics } from './ProfessionalMetrics';
import { NavigationSidebar } from './NavigationSidebar';
import { ExtendedMarketResearch } from '../types';

interface Props {
  hotelId?: string;
}

const MarketResearch: React.FC<Props> = ({ hotelId }) => {
  const { data, loading, error, generateMarketResearch, deleteMarketResearch } = useMarketResearch(hotelId);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const reportRef = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState('executive-summary');
  const [completedSections, setCompletedSections] = useState<string[]>([]);
  const [isScrolling, setIsScrolling] = useState(false);

  const handlePrint = useReactToPrint({
    documentTitle: 'Hotel Market Research Analysis',
    pageStyle: `
      @page {
        size: A4;
        margin: 15mm 20mm 15mm 20mm;
      }
      @media print {
        body {
          font-size: 12px;
          line-height: 1.4;
          color: #000 !important;
        }
        h1, h2, h3, h4, h5, h6 {
          color: #1565c0 !important;
          break-after: avoid;
        }
        .chart-container {
          break-inside: avoid;
        }
      }
    `,
    contentRef: reportRef,
  });

  // Handle scroll to track active section
  useEffect(() => {
    const handleScroll = () => {
      if (isScrolling) return;

      const sections = [
        'executive-summary',
        'market-overview',
        'competitive-analysis',
        'supply-analysis',
        'economic-indicators',
        'recommendations'
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetBottom = offsetTop + element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolling]);

  // Handle section navigation
  const handleSectionClick = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      setIsScrolling(true);
      setActiveSection(sectionId);

      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });

      // Reset scrolling flag after animation
      setTimeout(() => setIsScrolling(false), 1000);
    }
  };

  // Update completed sections based on actual data
  useEffect(() => {
    if (data.length > 0 && data[0].research_data) {
      const research = data[0].research_data;
      const mo: any = research.marketOverview || {};
      const completed: string[] = [];

      // Executive Summary is considered complete only if numeric marketSize & growthRate exist
      if (mo.marketSize && mo.growthRate) {
        completed.push('executive-summary');
      }

      // Market Overview – demandDrivers AND seasonalityData present
      if ((mo.demandDrivers?.length || 0) > 0 && (mo.seasonalityData?.length || 0) > 0) {
        completed.push('market-overview');
      }

      // Competitive Analysis – at least one competitor listed
      if ((research.competitiveAnalysis?.competitiveSet?.length || 0) > 0) {
        completed.push('competitive-analysis');
      }

      // Supply Analysis – pipelineProjects or supplyGrowth string exists
      if ((research.supplyAnalysis?.pipelineProjects?.length || 0) > 0 || !!research.supplyAnalysis?.supplyGrowth) {
        completed.push('supply-analysis');
      }

      // Economic Indicators – tourismOutlook string present and contains a number
      if (research.economicIndicators?.tourismOutlook?.length) {
        completed.push('economic-indicators');
      }

      // Recommendations – at least one target segment
      if ((research.recommendations?.targetSegments?.length || 0) > 0) {
        completed.push('recommendations');
      }

      setCompletedSections(completed);
    }
  }, [data]);

  const handleExport = () => {
    // Export to PDF using print functionality
    handlePrint();
  };

  return (
    <>
      {/* Print-only headers */}
      <PrintHeader />
      <PrintFooter />

      <Box sx={{
        display: 'flex',
        minHeight: 'calc(100vh - 64px)', // Account for MainLayout AppBar
        backgroundColor: theme.palette.background.default,
        position: 'relative',
        width: '100%',
        boxSizing: 'border-box',
        // Removed overflow to allow natural page scrolling
        alignItems: 'flex-start' // Ensure proper alignment for sticky sidebar
      }}>
        {/* Navigation Sidebar */}
        <NavigationSidebar
          activeSection={activeSection}
          onSectionClick={handleSectionClick}
          completedSections={completedSections}
        />

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            ml: 0,
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: theme.palette.background.default,
            position: 'relative',
            transition: 'margin-left 0.3s ease',
            // Allow viewport scrolling so sidebar stickiness works
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            width: '100%', // Use full width
            maxWidth: '100%' // Ensure no constraints
          }}
        >
          {/* Header */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1100,
              backgroundColor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(10px)',
              borderBottom: `1px solid ${theme.palette.divider}`,
              width: '100%',
              boxSizing: 'border-box' // Include padding in width calculation
            }}
          >
            <Box sx={{ py: 1.5, px: { xs: 1, sm: 1.5, md: 2, lg: 2.5 } }}>
              <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                <ReportHeader
                  title="Market Research Analysis"
                  subtitle="Comprehensive hotel market intelligence report"
                  marketResearch={data}
                />
                <ActionBar
                  onGenerate={generateMarketResearch}
                  onPrint={handlePrint}
                  onExport={handleExport}
                  loading={loading}
                  hasData={data.length > 0}
                />
              </Box>
            </Box>
          </Box>

          {/* Content Area */}
          <Box
            sx={{
              py: 2,
              px: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
              maxWidth: '100%', // Use full width
              width: '100%'
            }}
          >
            {/* Professional Metrics Overview */}
            {data.length > 0 && !loading && (
              <Fade in timeout={800}>
                <Box sx={{ mb: 4 }}>
                  <ProfessionalMetrics data={data[0]} />
                </Box>
              </Fade>
            )}

            {/* Loading State */}
            {loading && (
              <Box sx={{ mb: 4 }}>
                <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 2 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 2 }}>
                  {[1, 2, 3, 4].map((i) => (
                    <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Report Content */}
            <Box
              sx={{
                '& > *': {
                  scrollMarginTop: '80px'
                }
              }}
            >
              <ReportContent
                marketResearch={data}
                error={error}
                loading={loading}
                reportRef={reportRef}
                onDeleteReport={deleteMarketResearch}
              />
            </Box>

            {/* Empty State */}
            {data.length === 0 && !loading && (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 8,
                  px: 4,
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[1]
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    mb: 2,
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  No Market Research Available
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4 }}
                >
                  Generate comprehensive market research to analyze competitive positioning,
                  market dynamics, and strategic opportunities for your hotel property.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Print Styles */}
          <style>{`
            @media print {
              body {
                font-family: 'Inter', sans-serif !important;
                color: #000 !important;
                background: #fff !important;
              }

              .MuiDrawer-root,
              .print-hide {
                display: none !important;
              }

              .print-container {
                break-inside: avoid;
                page-break-inside: avoid;
                margin-bottom: 20px;
              }

              .print-section {
                break-after: page;
              }

              h1, h2, h3, h4, h5, h6 {
                color: #000 !important;
                page-break-after: avoid;
              }

              .MuiTypography-root {
                color: #000 !important;
              }

              .MuiPaper-root {
                box-shadow: none !important;
                border: 1px solid #e0e0e0 !important;
              }
            }
          `}</style>
        </Box>
      </Box>
    </>
  );
};

export default MarketResearch;
